/* eslint-disable no-underscore-dangle */
import { $t } from '@/i18n';
import { useStore } from '@/store';
import { hasCompleteArtifactCos, loadArtifactCosStylesheets, loadArtifactStylesheets, preloadArtifactCosAssets, disposeArtifactCosStylesheets, resolveRemoteRuntimeEntry } from '@/utils/aiProjectInfo';
import { useGogoRemoteCssReset } from './css-reset-counter';
import { useSetupAICheck } from './check/setup';
import { readGogoRemotePreviewConnection } from './gogoRemotePreviewConnection';
import { AsyncGogoRemoteHost, loadGogoLib, loadVue3Stuff } from './runtime-loader';
import RuntimeLoading from './RuntimeLoading.vue';
import { computed, defineComponent, onMounted, onUnmounted, provide, ref, shallowRef } from '@vue/composition-api';
import { getWujiBaseModules } from './useGogoRuntimeEnv';
import { notifyVue3RouterChanged } from './vue3router';
import useAiDepLoader from './xy-logic/use-ai-dep-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
import { createDebug } from 'obug';
import { resolveI18nField } from './utils/i18n-field';
const debug = createDebug('wuji-gogo:xy-client:GogoRuntimeRemoteWrapper');
const GOGO_CTX_MODULES_KEY = '__GOGO_CTX_MODULES__';
export default defineComponent({
    name: 'GogoRuntimeRemoteWrapper',
    components: {
        AsyncGogoRemoteHost,
        RuntimeLoading,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        env: {
            type: String,
            required: true,
        },
        branch: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        useGogoRemoteCssReset();
        const { markReady } = useSetupAICheck();
        const { loadAIPageDependency } = useAiDepLoader();
        const gogoRuntimeViewInstances = shallowRef([]);
        const routeViewContainer = shallowRef(null);
        const remoteHost = shallowRef(null);
        const src = ref('');
        const search = new URLSearchParams(window.location.search);
        const { wsUrl } = readGogoRemotePreviewConnection();
        const hasPreviewBridge = !!search.get('wujiAiPageUK');
        // iframe=1 只表示「编辑器内预览」。顶层打开同一条 URL 时没有 MessagePort 对端，
        // 必须走 wsUrl 的 devenv 桥，否则会一直停在「正在加载页面预览」。
        const isPreviewIFrame = hasPreviewBridge && search.get('iframe') === '1' && window.parent !== window;
        const useLiveWs = Boolean(wsUrl);
        // RouteView 通过 Teleport 渲染；用目标容器的 v-show 防闪烁，不阻止宿主加载 JS。
        const gogoHostReady = ref(isPreviewIFrame || useLiveWs);
        const loadStatus = ref((isPreviewIFrame || useLiveWs) ? 'ready' : 'loading');
        const loadError = ref('');
        // 编辑器预览由外层 PreviewPanel 统一展示右上角 loading，避免 iframe 内再出现中央圈圈。
        const remoteStartupLoading = computed(() => !isPreviewIFrame && loadStatus.value !== 'error'
            && (loadStatus.value === 'loading' || !remoteHost.value));
        let dependenciesPromise = Promise.resolve();
        let disposed = false;
        let blobSrc = '';
        let styleBlobs = [];
        provide('gogoRuntimeViewInstances', gogoRuntimeViewInstances);
        provide('routeViewContainer', routeViewContainer);
        provide('gogoHostReady', gogoHostReady);
        provide('gogoRemoteStartupLoading', remoteStartupLoading);
        provide('gogoPortalHostClass', 'wujiGogo-remote-host');
        function handleHostReady(host) {
            remoteHost.value = host;
            if (gogoHostReady.value)
                markReady();
            syncGogoHijackFlag(host);
            watchPageDescriptors(host);
        }
        // 与 GogoRuntimeRouterWrapper 对齐：预览态(存在 __gogoDebugHijack)时，把当前是否命中 gogo 页
        // 同步给 hijack 组件，让根路径 '/' 的 hijack 占位能渲染出 AI 页面。
        // 必须用 vue3 的 watchEffect 才能追踪 remote host 的 vue3 响应式 currentMatchedRoute。
        function syncGogoHijackFlag(host) {
            const gogoHijack = window.__gogoDebugHijack;
            if (!gogoHijack)
                return;
            void loadVue3Stuff().then((stuff) => {
                const { watchEffect } = stuff.vue;
                watchEffect(() => {
                    gogoHijack.currentRouteIsGogoPage = host.context?.currentMatchedRoute?.value?.descriptor?.file || '';
                });
            });
        }
        // pageDescriptors 是 vue3 ref；HMR/新增页时会更新，需用 vue3 watch 才能追踪。
        function watchPageDescriptors(host) {
            void loadVue3Stuff().then((stuff) => {
                const { watch, watchEffect } = stuff.vue;
                watch(() => ({
                    pages: host.context?.pageDescriptors?.value,
                    status: host.context?.hostStatus?.value,
                }), ({ pages, status }) => {
                    debug('pages', pages, status);
                    if (Array.isArray(pages)) {
                        // remote 的 hostStatus 是 idle/loading/loaded/error，等价于本地 executed 的是 loaded
                        notifyVue3RouterChanged({
                            pages,
                            useRemoteRuntime: true,
                            routeTableReady: status === 'loaded',
                        });
                    }
                }, { deep: true, immediate: true });
                // 追踪 currentMatchedRoute，对齐 GogoRuntimeRouterWrapper 的 i18nPatch 逻辑
                watchEffect(() => {
                    const gogoPage = host.context?.currentMatchedRoute?.value?.gogoPage;
                    const rawMeta = (gogoPage?.meta ?? {});
                    const title = resolveI18nField({ raw: rawMeta.title, patchJson: rawMeta.i18nPatch?.title });
                    const description = resolveI18nField({
                        raw: rawMeta.description,
                        patchJson: rawMeta.i18nPatch?.description,
                    });
                    const metaKeywords = resolveI18nField({
                        raw: rawMeta.metaKeywords,
                        patchJson: rawMeta.i18nPatch?.metaKeywords,
                    });
                    // 没有渲染的时候，不需要更新 title 和 description
                    if (!gogoRuntimeViewInstances.value.length)
                        return;
                    if (title) {
                        document.title = title;
                    }
                    if (description) {
                        let el = document.querySelector('meta[name="description"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'description');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', description);
                    }
                    if (metaKeywords) {
                        let el = document.querySelector('meta[name="keywords"]');
                        if (!el) {
                            el = document.createElement('meta');
                            el.setAttribute('name', 'keywords');
                            document.head.appendChild(el);
                        }
                        el.setAttribute('content', metaKeywords);
                    }
                });
            });
        }
        function handlePointerDown(e) {
            remoteHost.value?.handlePointerDown?.(e);
        }
        function abortEventIfPicking(e) {
            remoteHost.value?.abortEventIfPicking?.(e);
        }
        const beforeLoadRemoteModule = async () => {
            // 旧编辑器的 ESM 产物仍依赖 `@wuji-gogo/runtime`（patchVueSFCExports 等）。
            const [gogoRuntime] = await Promise.all([loadGogoLib('runtime'), dependenciesPromise]);
            window[GOGO_CTX_MODULES_KEY] = {
                ...window[GOGO_CTX_MODULES_KEY] || {},
                ...getWujiBaseModules({ projectId: props.projectId }),
                ...(window[WUJI_DEPS_KEY] || {}),
                '@wuji-gogo/runtime': gogoRuntime.runtimeShared,
            };
        };
        onMounted(async () => {
            if (isPreviewIFrame || useLiveWs)
                return;
            try {
                const store = useStore();
                // 与 aiProjectInfo 并行，不额外占用首屏 RTT；拉不到时按非云端模式加载依赖，
                // 多加载一次好过让整个页面加载失败
                const aiConfigPromise = store.dispatch('projectAiConfig/ensure', {
                    projectId: props.projectId, runtime: true, envId: props.env,
                }).catch(e => console.warn('[GogoRuntimeRemoteWrapper] load ai config failed', e));
                const aiProjectInfo = await store.dispatch('runtime/getAiProjectInfo', {
                    projectId: props.projectId,
                    env: props.env,
                    branchId: props.branch,
                });
                if (disposed)
                    return;
                const entry = resolveRemoteRuntimeEntry(aiProjectInfo);
                if (!entry) {
                    loadError.value = $t('k_181fbe72');
                    loadStatus.value = 'error';
                    return;
                }
                if (entry.fromBlob)
                    blobSrc = entry.src;
                let stylesPromise;
                if (hasCompleteArtifactCos(aiProjectInfo)) {
                    // 样式不等待 AI 配置；页面 chunk 仍由 defineAsyncComponent 按需 import。
                    preloadArtifactCosAssets(aiProjectInfo.artifactCos);
                    stylesPromise = loadArtifactCosStylesheets(aiProjectInfo.artifactCos);
                }
                else {
                    stylesPromise = loadArtifactStylesheets(aiProjectInfo?.artifact).then((hrefs) => {
                        if (disposed) {
                            hrefs.forEach((href) => {
                                document.head.querySelectorAll('link[rel="stylesheet"]')
                                    .forEach((link) => {
                                    if (link.getAttribute('href') === href)
                                        link.remove();
                                });
                                URL.revokeObjectURL(href);
                            });
                        }
                        else {
                            styleBlobs = hrefs;
                        }
                    });
                }
                dependenciesPromise = aiConfigPromise.then((config) => {
                    if (config?.useRemoteEditor !== true)
                        return loadAIPageDependency(true, 'vue3');
                });
                // 提前创建宿主；beforeLoadRemoteModule 只等待共享依赖，不等待项目 CSS。
                src.value = entry.src;
                await Promise.all([stylesPromise, dependenciesPromise]);
                if (disposed)
                    return;
                gogoHostReady.value = true;
                if (remoteHost.value)
                    markReady();
                loadStatus.value = 'ready';
            }
            catch (error) {
                if (disposed)
                    return;
                console.error('[xy-client] load aiProjectInfo for remote runtime failed', error);
                loadError.value = $t('k_6be6212c', {
                    arg_0: error instanceof Error ? error.message : String(error),
                });
                loadStatus.value = 'error';
            }
        });
        onUnmounted(() => {
            disposed = true;
            disposeArtifactCosStylesheets();
            styleBlobs.forEach(href => URL.revokeObjectURL(href));
            if (blobSrc)
                URL.revokeObjectURL(blobSrc);
        });
        return {
            wsUrl,
            hasPreviewBridge,
            src,
            isPreviewIFrame,
            useLiveWs,
            loadStatus,
            loadError,
            remoteStartupLoading,
            routeViewContainer,
            handleHostReady,
            handlePointerDown,
            abortEventIfPicking,
            beforeLoadRemoteModule,
        };
    },
});
